import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Input, Row, Space, Typography, Upload, notification } from "antd";
import { CheckOutlined, UploadOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { Container } from "components/Container";
import DocumentoSolicitableService from "services/DocumentoSolicitableService";

const { Text } = Typography;

const RUTA_LISTADO = "/configuracion/documentos-solicitables/listar";

const VARIABLES = [
  "${COLABORADOR_NOMBRE_COMPLETO}",
  "${COLABORADOR_NUMERO_DOCUMENTO_IDENTIDAD}",
  "${COLABORADOR_CARGO}",
  "${COLABORADOR_AREA}",
  "${COLABORADOR_FECHA_INGRESO}",
  "${COLABORADOR_FECHA_CESE}",
  "<FECHA_EMISION>",
];

const FormularioDocumentoSolicitable = ({ idDocumento }) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [archivos, setArchivos] = useState([]);
  const [nombreArchivoActual, setNombreArchivoActual] = useState(undefined);
  const esEdicion = !!idDocumento;

  useEffect(() => {
    if (!esEdicion) return;
    const cargarDocumento = async () => {
      try {
        const documento = await DocumentoSolicitableService.buscarPorId(idDocumento);
        form.setFieldsValue({ nombre: documento.nombre, descripcion: documento.descripcion });
        setNombreArchivoActual(documento.nombreArchivoPlantilla);
      } catch (error) {
        console.error(error);
      }
    };
    cargarDocumento();
  }, [idDocumento]);

  const onFinish = async (formulario) => {
    if (!esEdicion && archivos.length === 0) {
      notification.error({ message: "Plantilla requerida", description: "Adjunte la plantilla Word (.docx)." });
      return;
    }
    const datos = {
      nombre: formulario.nombre,
      descripcion: formulario.descripcion,
      archivo: archivos.length > 0 ? archivos[0] : undefined,
    };
    try {
      setLoading(true);
      if (esEdicion) {
        await DocumentoSolicitableService.actualizar(idDocumento, datos);
      } else {
        await DocumentoSolicitableService.insertar(datos);
      }
      notification.success({
        message: "Operación exitosa",
        description: esEdicion ? "Se actualizó el documento correctamente." : "Se registró el documento correctamente.",
      });
      history.push(RUTA_LISTADO);
    } catch (error) {
      console.error(error);
      notification.error({
        message: "No se pudo guardar el documento",
        description: error?.response?.data?.mensaje || error?.response?.data?.message || "Intente nuevamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const propsUpload = {
    accept: ".docx",
    maxCount: 1,
    fileList: archivos,
    beforeUpload: (archivo) => {
      if (!archivo.name.toLowerCase().endsWith(".docx")) {
        notification.error({ message: "Formato no válido", description: "La plantilla debe ser un archivo .docx" });
        return Upload.LIST_IGNORE;
      }
      setArchivos([archivo]);
      return false;
    },
    onRemove: () => setArchivos([]),
  };

  return (
    <Container
      titulo={esEdicion ? "Editar Documento Solicitable" : "Crear Documento Solicitable"}
      onClickRetornar={() => history.push(RUTA_LISTADO)}
    >
      <Row justify="center">
        <Col xs={24} md={14} lg={12}>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              label="Nombre del documento"
              name="nombre"
              rules={[{ required: true, whitespace: true, message: "Escribe el nombre del documento" }]}
            >
              <Input placeholder="Ej. Constancia de Trabajo" maxLength={100} autoComplete="off" />
            </Form.Item>
            <Form.Item label="Descripción" name="descripcion">
              <Input placeholder="Descripción (opcional)" maxLength={200} autoComplete="off" />
            </Form.Item>
            <Form.Item
              label="Plantilla Word (.docx)"
              required={!esEdicion}
              extra={
                esEdicion && nombreArchivoActual
                  ? `Plantilla actual: ${nombreArchivoActual}. Adjunte un archivo solo si desea reemplazarla.`
                  : undefined
              }
            >
              <Upload {...propsUpload}>
                <Button icon={<UploadOutlined />}>Seleccionar archivo</Button>
              </Upload>
            </Form.Item>
            <Alert
              style={{ marginBottom: 24 }}
              type="info"
              showIcon
              message="Variables disponibles en la plantilla"
              description={
                <Space direction="vertical" size={2}>
                  <Text>Escríbalas tal cual en el Word y se reemplazarán con los datos del colaborador:</Text>
                  {VARIABLES.map((variable) => (
                    <Text code key={variable}>
                      {variable}
                    </Text>
                  ))}
                </Space>
              }
            />
            <Row justify="center">
              <Space>
                <Button onClick={() => history.push(RUTA_LISTADO)}>Cancelar</Button>
                <Button type="primary" icon={<CheckOutlined />} loading={loading} htmlType="submit">
                  {esEdicion ? "Guardar cambios" : "Crear documento"}
                </Button>
              </Space>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export const CrearDocumentoSolicitablePage = () => <FormularioDocumentoSolicitable />;

export const EditarDocumentoSolicitablePage = () => {
  const { idDocumento } = useParams();
  return <FormularioDocumentoSolicitable idDocumento={idDocumento} key={idDocumento} />;
};
